import { useEffect, useState } from 'react';
import {
  App,
  Button,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getTagFindAllQueryKey,
  tagCreate,
  useTagFindAll,
  useTagSetPatientTags,
} from '../../libs/ohat-api';
import { TagDto } from '@models/tagDto';
import { useQueryClient } from '@tanstack/react-query';
import { getApiErrorMessage } from '../../utils/apiError';

type Props = {
  open: boolean;
  patientId: string;
  /** 患者に現在付与されているタグ */
  currentTags: { id: string; name: string }[];
  onClose: () => void;
  /** 保存完了後に親側で患者情報を再取得するためのコールバック */
  onSaved: () => void;
};

/**
 * 患者へのタグ付け替えモーダル（全置換・ライトプラン限定）。
 * 既存タグの選択（プルダウン）と新規タグの作成（入力＋「追加」）を分離し、
 * プルダウンでは Enter による選択を行わない（選択はクリックのみ）。
 */
export const TagEditModal = ({
  open,
  patientId,
  currentTags,
  onClose,
  onSaved,
}: Props) => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: allTags } = useTagFindAll({ query: { enabled: open } });
  const { mutateAsync: setPatientTags } = useTagSetPatientTags();
  /** 既存タグの選択値（タグIDのみ） */
  const [values, setValues] = useState<string[]>([]);
  /** 保存時に作成する新規タグ名 */
  const [pendingNames, setPendingNames] = useState<string[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [saving, setSaving] = useState(false);

  // モーダルを開くたびに現在のタグで初期化する（前回の入力を持ち越さない）
  useEffect(() => {
    if (open) {
      setValues(currentTags.map((tag) => tag.id));
      setPendingNames([]);
      setNewTagName('');
    }
  }, [open, currentTags]);

  const tags: TagDto[] = allTags ?? [];

  /**
   * 入力中の名称を保存待ちの新規タグとして追加する。
   * 空白のみは無視し、既存タグと同名なら新規作成せずその既存タグを選択に加える。
   */
  const handleAddPending = () => {
    const name = newTagName.trim();
    if (!name) return;
    const existing = tags.find((tag) => tag.name === name);
    if (existing) {
      setValues((prev) =>
        prev.includes(existing.id) ? prev : [...prev, existing.id],
      );
      setNewTagName('');
      return;
    }
    // 保存待ちリスト内での重複は積まない
    if (!pendingNames.includes(name)) {
      setPendingNames((prev) => [...prev, name]);
    }
    setNewTagName('');
  };

  const handleRemovePending = (name: string) => {
    setPendingNames((prev) => prev.filter((pending) => pending !== name));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // 「追加」を押さずに保存しても入力したタグ名を取りこぼさないよう、
      // 入力欄に残っている名称にも handleAddPending と同じ判定を適用する。
      // setState の反映を待たずに済むよう、対象リストはこの場で組み立てる。
      const typedName = newTagName.trim();
      const selectedIds = [...values];
      const namesToCreate = [...pendingNames];
      if (typedName) {
        const existing = tags.find((tag) => tag.name === typedName);
        if (existing) {
          selectedIds.push(existing.id);
        } else if (!namesToCreate.includes(typedName)) {
          namesToCreate.push(typedName);
        }
      }
      // 保存待ちの新規タグを作成し、既存選択IDと結合する（重複は除去）
      const createdIds: string[] = [];
      for (const name of namesToCreate) {
        const created = (await tagCreate({ name })) as { id: string };
        createdIds.push(created.id);
      }
      const tagIds = Array.from(new Set([...selectedIds, ...createdIds]));
      await setPatientTags({ id: patientId, data: { tagIds } });
      queryClient.invalidateQueries({ queryKey: getTagFindAllQueryKey() });
      message.success(t('tagEdit.updated'));
      onSaved();
      onClose();
    } catch (e) {
      console.error('tag update error:', e);
      message.error(getApiErrorMessage(e, t('tagEdit.updateFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t('tagEdit.title')}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={saving}
      afterClose={() => {
        setValues([]);
        setPendingNames([]);
        setNewTagName('');
      }}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t('tagEdit.description')}
      </Typography.Paragraph>
      <Select
        mode="multiple"
        style={{ width: '100%' }}
        placeholder={t('tagEdit.placeholder')}
        value={values}
        onChange={setValues}
        options={tags.map((tag) => ({ label: tag.name, value: tag.id }))}
        // Enter でオプションを選択させない（選択はクリックのみ）。
        // input の keydown で stopPropagation することで、イベントが rc-select の
        // BaseSelect#onInternalKeyDown → OptionList#onKeyDown（Enter で activeIndex の
        // オプションを選択する）へバブルせず、IME 変換確定の Enter による誤選択も防げる。
        onInputKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        filterOption={(input, option) =>
          String(option?.label ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />
      <Space.Compact style={{ width: '100%', marginTop: 12 }}>
        <Input
          value={newTagName}
          placeholder={t('tagEdit.newTagPlaceholder')}
          onChange={(e) => setNewTagName(e.target.value)}
          // IME 変換中（変換確定）の Enter は追加とみなさない
          onPressEnter={(e) => {
            if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) {
              return;
            }
            e.preventDefault();
            handleAddPending();
          }}
        />
        <Button type="primary" onClick={handleAddPending}>
          {t('tagEdit.add')}
        </Button>
      </Space.Compact>
      {pendingNames.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, display: 'block' }}
          >
            {t('tagEdit.pendingLabel')}
          </Typography.Text>
          <Space size={[8, 8]} wrap style={{ marginTop: 4 }}>
            {pendingNames.map((name) => (
              <Tag
                key={name}
                closable
                color="blue"
                onClose={() => handleRemovePending(name)}
              >
                {name}
              </Tag>
            ))}
          </Space>
        </div>
      )}
    </Modal>
  );
};
